import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Plus, Loader2, Leaf, X, Info } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

export default function SideSelectionModal({ 
  isOpen, 
  onClose, 
  menuItem, 
  onAddToCart,
  deliveryDate 
}) {
  const [sidesData, setSidesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSides, setSelectedSides] = useState([]);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (isOpen && menuItem) {
      fetchSides();
    } else {
      // Reset when closed
      setSelectedSides([]);
      setSidesData(null);
    }
  }, [isOpen, menuItem]);

  const fetchSides = async () => {
    setLoading(true);
    setSelectedSides([]);
    try {
      const res = await fetch(`${API_URL}/api/menu/${menuItem.item_id || menuItem.meal_id}/sides`);
      if (res.ok) {
        const data = await res.json();
        setSidesData(data);
      }
    } catch (error) {
      console.error('Error fetching sides:', error);
      toast.error('Failed to load sides');
    } finally {
      setLoading(false);
    }
  };

  const toggleSide = (side) => {
    setSelectedSides(prev => {
      const exists = prev.find(s => s.side_id === side.side_id);
      if (exists) return prev.filter(s => s.side_id !== side.side_id);
      return [...prev, side];
    });
  };

  const freeCount = sidesData?.free_sides_count ?? 1;
  const extraPrice = sidesData?.additional_side_price || 0;
  const perSide = !!sidesData?.per_side_pricing;

  const sidePrice = (side) => (perSide ? (side.price || 0) : extraPrice);

  const calculateSidesTotal = () => {
    if (perSide) return selectedSides.reduce((sum, s) => sum + (s.price || 0), 0);
    const extraCount = Math.max(0, selectedSides.length - freeCount);
    return extraCount * extraPrice;
  };

  const sidesTotal = calculateSidesTotal();
  const totalPrice = (menuItem?.price || 0) + sidesTotal;
  const hasRequiredSides = sidesData?.has_sides && sidesData?.sides?.length > 0;
  const sidesRequired = hasRequiredSides && freeCount > 0;
  const canProceed = !sidesRequired || selectedSides.length >= 1;

  const handleAdd = () => {
    if (!sidesData) return;
    if (selectedSides.length < 1 && sidesRequired) {
      toast.error('Please select at least 1 side');
      return;
    }

    const processedSides = selectedSides.map((side, index) => ({
      side_id: side.side_id,
      name: side.name,
      price: perSide ? (side.price || 0) : (index < freeCount ? 0 : extraPrice),
      is_extra: perSide ? true : index >= freeCount
    }));

    onAddToCart({
      ...menuItem,
      selected_sides: processedSides,
      sides_total: sidesTotal,
      delivery_date: deliveryDate
    });
    onClose();
  };

  const handleSkip = () => {
    onAddToCart({
      ...menuItem,
      selected_sides: [],
      sides_total: 0,
      delivery_date: deliveryDate
    });
    onClose();
  };

  if (!menuItem) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 z-[70]"
          />

          {/* Bottom sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 32, stiffness: 320 }}
            className="fixed inset-x-0 bottom-0 z-[70] bg-white flex flex-col rounded-t-3xl"
            style={{ maxHeight: '92dvh' }}
            data-testid="side-selection-modal"
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>

            {/* Item header */}
            <div className="relative flex-shrink-0">
              {menuItem.image_url && (
                <div className="h-44 overflow-hidden">
                  <img
                    src={menuItem.image_url}
                    alt={menuItem.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                </div>
              )}
              <button
                onClick={onClose}
                className="absolute top-3 right-4 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-sm z-10"
              >
                <X className="h-4 w-4 text-gray-700" />
              </button>
              <div className={menuItem.image_url ? 'absolute bottom-0 left-0 right-0 p-4' : 'px-5 pt-3 pb-2'}>
                <h2 className={`font-heading text-xl font-bold ${menuItem.image_url ? 'text-white' : 'text-gray-900'}`}>
                  {menuItem.name}
                </h2>
                <p className={`text-base font-semibold mt-0.5 ${menuItem.image_url ? 'text-white/90' : 'text-primary'}`}>
                  R{(menuItem.default_price || menuItem.price)?.toFixed(2)}
                </p>
              </div>
            </div>

            {/* Scrollable sides content */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="ml-3 text-gray-500">Loading options...</span>
                </div>
              ) : hasRequiredSides ? (
                <div>
                  {/* Section header */}
                  <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-y border-gray-100">
                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm">{sidesRequired ? 'Choose your side' : 'Add a side?'}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {sidesRequired
                          ? `${freeCount} included free${extraPrice > 0 ? ` • Extra R${extraPrice.toFixed(2)} each` : ''}`
                          : perSide
                            ? 'Optional • priced per side'
                            : `Optional • R${extraPrice.toFixed(2)} each`}
                      </p>
                    </div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                      selectedSides.length >= 1
                        ? 'bg-green-100 text-green-700'
                        : sidesRequired
                          ? 'bg-red-100 text-red-600'
                          : 'bg-gray-100 text-gray-500'
                    }`}>
                      {selectedSides.length >= 1 ? `${selectedSides.length} selected` : sidesRequired ? 'Required' : 'Optional'}
                    </span>
                  </div>

                  {/* Sides list - full scrollable */}
                  <div className="divide-y divide-gray-100">
                    {sidesData.sides.map((side, index) => {
                      const isSelected = !!selectedSides.find(s => s.side_id === side.side_id);
                      const selectionIndex = selectedSides.findIndex(s => s.side_id === side.side_id);
                      const isExtra = selectionIndex >= freeCount && selectionIndex !== -1;

                      return (
                        <button
                          key={side.side_id}
                          onClick={() => toggleSide(side)}
                          className={`w-full flex items-center gap-4 px-5 py-4 text-left transition-colors ${
                            isSelected ? 'bg-primary/5' : 'hover:bg-gray-50 active:bg-gray-100'
                          }`}
                          data-testid={`side-option-${side.side_id}`}
                        >
                          {/* Side image */}
                          <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                            {side.image_url ? (
                              <img src={side.image_url} alt={side.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Leaf className="h-6 w-6 text-gray-300" />
                              </div>
                            )}
                          </div>

                          {/* Side details */}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900">{side.name}</p>
                            {side.description && (
                              <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{side.description}</p>
                            )}
                            {side.dietary_tags?.length > 0 && (
                              <div className="flex gap-1 mt-1.5 flex-wrap">
                                {side.dietary_tags.slice(0, 2).map(tag => (
                                  <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Price + checkbox */}
                          <div className="flex items-center gap-3 flex-shrink-0">
                            {isSelected && (perSide || isExtra) && (
                              <span className="text-sm font-semibold text-amber-600">+R{sidePrice(side).toFixed(2)}</span>
                            )}
                            {!isSelected && selectionIndex === -1 && sidePrice(side) > 0 && (perSide || selectedSides.length >= freeCount) && (
                              <span className="text-xs text-gray-400">+R{sidePrice(side).toFixed(2)}</span>
                            )}
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                              isSelected
                                ? isExtra
                                  ? 'bg-amber-500 border-amber-500'
                                  : 'bg-primary border-primary'
                                : 'border-gray-300'
                            }`}>
                              {isSelected && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Validation hint */}
                  {sidesRequired && selectedSides.length < 1 && (
                    <div className="mx-5 my-3 flex items-center gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200">
                      <Info className="h-4 w-4 text-amber-600 flex-shrink-0" />
                      <p className="text-sm text-amber-700">Select a side to add this item to your cart</p>
                    </div>
                  )}

                  {/* Extra padding at bottom for breathing room */}
                  <div className="h-4" />
                </div>
              ) : !loading ? (
                <div className="px-5 py-10 text-center">
                  <Leaf className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 mb-4">No sides configured for this item</p>
                  <button
                    onClick={handleSkip}
                    className="w-full h-12 bg-primary text-white rounded-2xl font-semibold"
                  >
                    Add to Cart — R{(menuItem.default_price || menuItem.price)?.toFixed(2)}
                  </button>
                </div>
              ) : null}
            </div>

            {/* Fixed bottom button */}
            {hasRequiredSides && !loading && (
              <div className="flex-shrink-0 border-t border-gray-100 px-5 pt-4 pb-6 bg-white">
                {/* Price summary */}
                {sidesTotal > 0 && (
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                    <span>Item + sides</span>
                    <span>R{(menuItem.default_price || menuItem.price)?.toFixed(2)} + R{sidesTotal.toFixed(2)}</span>
                  </div>
                )}
                <button
                  onClick={handleAdd}
                  disabled={adding || !canProceed}
                  className={`w-full h-14 rounded-2xl font-bold text-base transition-all flex items-center justify-center gap-2 ${
                    canProceed
                      ? 'bg-primary text-white active:scale-[0.98]'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                  data-testid="add-with-sides-btn"
                >
                  {adding ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Plus className="h-5 w-5" strokeWidth={2.5} />
                      Add to Order — R{totalPrice.toFixed(2)}
                    </>
                  )}
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
